/**
 * UPB (unpaid principal balance) model for a PURCHASED loan — shared by the
 * Summary tab's UPB card / chart and the Trade economics tab.
 *
 * Every number here comes from a field on LoanAcquisitionBaseModel
 * (eastview-os app/models/loan_acquisitions.py) or is arithmetic on them:
 *   originalUPB      the amount at the correspondent close (whole note / tape)
 *   currentUPB       the balance we buy — what the trade is priced on
 *   cutOffDate       balances + accrued interest are as of this date
 *   paidToDate       accrued interest runs paidToDate → cutOffDate
 *   accruedInterest  · bidPricePercent · purchasePriceAmount · fundingWireAmount
 *   maturityDate     stored for a purchased loan, not derived
 *   amortizationType · noteRate (interestRate mirrors it)
 *   purchasedAsset.drawHistory  draws taken BEFORE the trade
 *
 * The schedule after the cut-off is computed from the note's terms — it is a
 * projection, not a servicer record.
 */
import { dateOnlyParts } from "../../../../_src_shared/utils/functions/formatDate";
import formatDollar from "../../../../_src_shared/utils/functions/formatDollar";

export const num = (v) => {
  const n = Number(v);
  return v == null || v === "" || !Number.isFinite(n) ? null : n;
};

/** A date-only field → local midnight on its stored calendar day. */
export const toDay = (v) => {
  const p = dateOnlyParts(v);
  return p ? new Date(p.y, p.m - 1, p.d) : null;
};

export const addMonths = (d, n) =>
  new Date(d.getFullYear(), d.getMonth() + n, d.getDate());

export const monthsBetween = (a, b) =>
  (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth());

export const daysBetween = (a, b) => Math.round((b - a) / 86400000);

export const shortDate = (d) =>
  d
    ? d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : null;

export const monthLabel = (d) =>
  d.toLocaleDateString("en-US", { month: "short", year: "numeric" });

/** Dollars, cents rounded off the float. */
export const money = (v, digits = 0) => {
  if (v == null) return null;
  const n = Math.round(v * 100) / 100;
  return digits
    ? `$${n.toLocaleString("en-US", {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      })}`
    : formatDollar(Math.round(n), null);
};

export const compactDollar = (v) => {
  const a = Math.abs(v);
  if (a >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `$${Math.round(v / 1e3)}k`;
  return `$${Math.round(v)}`;
};

/** Note rate as a decimal. interestRate mirrors noteRate on an acquisition. */
const rateOf = (loan) =>
  num(loan?.noteRate) ?? num(loan?.interestRate) ?? num(loan?.couponRate);

/** The facts every UPB surface reads — no surface re-derives these. */
export const upbFacts = (loan) => {
  const currentUPB = num(loan?.currentUPB);
  const originalUPB = num(loan?.originalUPB);
  const cutOff =
    toDay(loan?.cutOffDate) ||
    toDay(loan?.settlementDate) ||
    toDay(loan?.expectedSettlementDate);
  const paidTo = toDay(loan?.paidToDate);
  const bid = num(loan?.bidPricePercent);
  return {
    currentUPB,
    originalUPB,
    // + when the balance grew (draws), − when principal was paid down.
    change:
      currentUPB != null && originalUPB != null
        ? currentUPB - originalUPB
        : null,
    cutOff,
    cutOffIsFirm: !!loan?.cutOffDate,
    paidTo,
    accruedDays:
      paidTo && cutOff ? Math.max(0, daysBetween(paidTo, cutOff)) : null,
    accruedInterest: num(loan?.accruedInterest),
    bid,
    purchasePrice:
      num(loan?.purchasePriceAmount) ??
      (currentUPB != null && bid != null ? (currentUPB * bid) / 100 : null),
    purchasePriceIsStored: num(loan?.purchasePriceAmount) != null,
    fundingWire: num(loan?.fundingWireAmount),
    maturity: toDay(loan?.maturityDate),
    origination: toDay(loan?.originationDate),
    rate: rateOf(loan),
    amortizationType: loan?.amortizationType || null,
    fullyDrawn: num(loan?.totalLoanAmount),
  };
};

const isAmortizing = (type) =>
  type === "30-Year Fixed" || (type || "").startsWith("10yr IO");

/**
 * Month-by-month balance from `start` to `maturity`.
 * Interest-only: flat, balloon at maturity. Amortizing: level payment (the
 * baseline's — a curtailment keeps the payment and pays off sooner).
 * Returns the points, the interest earned and the balloon at maturity.
 */
const runSchedule = ({
  bal0,
  start,
  maturity,
  rate,
  ioEnd,
  amortizing,
  pmt,
  curtail,
}) => {
  const points = [{ date: start, value: bal0 }];
  const r = rate == null ? null : rate / 12;
  const total = Math.min(monthsBetween(start, maturity), 480);
  let bal = bal0;
  let payment = pmt;
  let interest = 0;
  let payoff = null;
  for (let m = 1; m <= total; m += 1) {
    const d = m === total ? maturity : addMonths(start, m);
    if (
      curtail &&
      d.getFullYear() === curtail.date.getFullYear() &&
      d.getMonth() === curtail.date.getMonth()
    ) {
      points.push({ date: d, value: bal });
      bal = Math.max(0, bal - curtail.amount);
      points.push({ date: d, value: bal, note: "After curtailment" });
    }
    if (r != null) interest += bal * r;
    if (amortizing && r != null && (!ioEnd || d > ioEnd)) {
      if (payment == null) {
        const n = Math.max(1, monthsBetween(d, maturity) + 1);
        payment = r === 0 ? bal / n : (bal * r) / (1 - (1 + r) ** -n);
      }
      bal = Math.max(0, bal - Math.min(bal, payment - bal * r));
    }
    if (bal <= 0.005) {
      points.push({ date: d, value: 0, note: "Paid off" });
      payoff = d;
      bal = 0;
      break;
    }
    if (m < total) points.push({ date: d, value: bal });
  }
  const balloon = payoff ? 0 : bal;
  if (!payoff) {
    points.push({ date: maturity, value: balloon, note: "At maturity" });
    points.push({ date: maturity, value: 0, note: "Paid off at maturity" });
  }
  return {
    points,
    interest: r == null ? null : interest,
    balloon,
    payoff: payoff || maturity,
    pmt: payment,
  };
};

/**
 * The chart series. `actual` runs origination → each pre-purchase draw →
 * cut-off; `scheduled` continues cut-off → maturity; `curtailed` is the
 * what-if with a one-time principal reduction.
 */
export const buildUpbSeries = (loan, curtail) => {
  const f = upbFacts(loan);
  const { currentUPB, originalUPB, origination, cutOff, maturity, rate } = f;
  if (currentUPB == null || !cutOff) return { ...f, upb: [] };

  const upb = [];
  if (origination && originalUPB != null && origination < cutOff) {
    let running = originalUPB;
    upb.push({ date: origination, value: originalUPB, note: "At origination" });
    (loan?.purchasedAsset?.drawHistory || [])
      .filter((d) => d?.drawKind !== "change_order")
      .map((d) => ({
        date: toDay(d?.drawDisbursedDate || d?.date),
        amount: num(d?.grossDrawAmount) ?? num(d?.drawRequestedAmount),
      }))
      .filter(
        (d) => d.date && d.amount && d.date > origination && d.date < cutOff,
      )
      .sort((a, b) => a.date - b.date)
      .forEach((d, i) => {
        upb.push({ date: d.date, value: running });
        running += d.amount;
        upb.push({
          date: d.date,
          value: running,
          note: `After draw ${i + 1} (${money(d.amount)})`,
        });
      });
  }
  upb.push({
    date: cutOff,
    value: currentUPB,
    note: "Purchased balance (cut-off)",
  });

  if (!maturity || maturity <= cutOff) return { ...f, upb, schedule: null };

  const amortizing = rate != null && isAmortizing(f.amortizationType);
  const ioEnd =
    (f.amortizationType || "").startsWith("10yr IO") && origination
      ? addMonths(origination, 120)
      : null;
  const base = runSchedule({
    bal0: currentUPB,
    start: cutOff,
    maturity,
    rate,
    ioEnd,
    amortizing,
  });
  upb.push(...base.points.slice(1));

  let curtailed = null;
  if (curtail?.amount > 0 && curtail?.date) {
    const run = runSchedule({
      bal0: currentUPB,
      start: cutOff,
      maturity,
      rate,
      ioEnd,
      amortizing,
      pmt: base.pmt,
      curtail,
    });
    const from = run.points.findIndex((p) => p.note === "After curtailment");
    curtailed = {
      ...run,
      points: from > 0 ? run.points.slice(from - 1) : [],
      interestReduced:
        base.interest == null || run.interest == null
          ? null
          : Math.max(0, base.interest - run.interest),
    };
  }

  return { ...f, upb, amortizing, schedule: base, curtailed };
};

/**
 * Whether the UPB pair (lead card + chart) has something real to show: both
 * UPBs on the loan and enough points to draw a line.
 */
export const hasUpbChart = (loan) =>
  num(loan?.currentUPB) != null &&
  num(loan?.originalUPB) != null &&
  buildUpbSeries(loan).upb.length >= 2;

/** Months a curtailment can land in: the month after cut-off → before maturity. */
export const curtailmentMonths = (cutOff, maturity) => {
  if (!cutOff || !maturity) return [];
  const out = [];
  for (let m = 1; m < Math.min(monthsBetween(cutOff, maturity), 480); m += 1) {
    const d = addMonths(
      new Date(cutOff.getFullYear(), cutOff.getMonth(), 1),
      m,
    );
    out.push({
      value: `${d.getFullYear()}-${d.getMonth() + 1}`,
      date: d,
      label: monthLabel(d),
    });
  }
  return out;
};
